import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Trash2, DollarSign, TrendingUp, Receipt, PiggyBank, Users, Pencil, Filter, Wallet } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { PaymentDialog, ProfitDialog, ContabilidadDeleteDialog } from '@/components/contabilidad/ContabilidadDialogs';
import PremiumStatCard from '@/components/premium/PremiumStatCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContabilidadPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [profits, setProfits] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');
  const [periodFilter, setPeriodFilter] = useState('all');
  
  // Payment dialog
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    gestor_id: '',
    amount: '',
    description: '',
    payment_date: new Date(),
    has_commission: false,
    commission_pct: '',
    commission: ''
  });
  const [pricingGuides, setPricingGuides] = useState([]);
  
  // Profit dialog
  const [isProfitDialogOpen, setIsProfitDialogOpen] = useState(false);
  const [editingProfit, setEditingProfit] = useState(null);
  const [profitForm, setProfitForm] = useState({
    amount: '',
    description: '',
    entry_date: new Date()
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [periodFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const periodParam = periodFilter !== 'all' ? `?period=${periodFilter}` : '';
      
      const [paymentsRes, profitsRes, gestoresRes, summaryRes, pricingRes] = await Promise.all([
        axios.get(`${API_URL}/payments${periodParam}`, { headers }),
        axios.get(`${API_URL}/profits${periodParam}`, { headers }),
        axios.get(`${API_URL}/gestores`, { headers }),
        axios.get(`${API_URL}/accounting/summary${periodParam}`, { headers }),
        axios.get(`${API_URL}/admin/pricing`, { headers })
      ]);
      
      setPayments(paymentsRes.data);
      setProfits(profitsRes.data);
      setGestores(gestoresRes.data);
      setSummary(summaryRes.data);
      setPricingGuides(pricingRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Sort gestores by total_sales descending
  const sortedGestores = [...gestores].sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0));

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        gestor_id: paymentForm.gestor_id,
        amount: parseFloat(paymentForm.amount),
        description: paymentForm.description,
        payment_date: format(paymentForm.payment_date, 'yyyy-MM-dd'),
        commission: paymentForm.commission ? parseFloat(paymentForm.commission) : null
      };

      if (editingPayment) {
        await axios.put(`${API_URL}/payments/${editingPayment.id}`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Pago actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/payments`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Pago registrado exitosamente');
      }
      
      fetchData();
      handleClosePaymentDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      gestor_id: payment.gestor_id,
      amount: payment.amount.toString(),
      description: payment.description || '',
      payment_date: new Date(payment.payment_date),
      has_commission: !!(payment.commission && payment.commission > 0),
      commission_pct: payment.commission && payment.amount ? ((payment.commission / payment.amount) * 100).toFixed(3) : '',
      commission: payment.commission?.toString() || ''
    });
    setIsPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setIsPaymentDialogOpen(false);
    setEditingPayment(null);
    setPaymentForm({ gestor_id: '', amount: '', description: '', payment_date: new Date(), has_commission: false, commission_pct: '', commission: '' });
  };

  const handleProfitSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        amount: parseFloat(profitForm.amount),
        description: profitForm.description,
        entry_date: format(profitForm.entry_date, 'yyyy-MM-dd')
      };

      if (editingProfit) {
        await axios.put(`${API_URL}/profits/${editingProfit.id}`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Ganancia actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/profits`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Ganancia registrada exitosamente');
      }
      
      fetchData();
      handleCloseProfitDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar ganancia');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProfit = (profit) => {
    setEditingProfit(profit);
    setProfitForm({
      amount: profit.amount.toString(),
      description: profit.description || '',
      entry_date: new Date(profit.entry_date)
    });
    setIsProfitDialogOpen(true);
  };

  const handleCloseProfitDialog = () => {
    setIsProfitDialogOpen(false);
    setEditingProfit(null);
    setProfitForm({ amount: '', description: '', entry_date: new Date() });
  };

  const handleDelete = async () => {
    try {
      const endpoint = deleteTarget.type === 'payment' 
        ? `${API_URL}/payments/${deleteTarget.id}`
        : `${API_URL}/profits/${deleteTarget.id}`;
      
      await axios.delete(endpoint, { headers: getAuthHeaders() });
      toast.success('Eliminado correctamente');
      fetchData();
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  const getPeriodLabel = () => {
    switch(periodFilter) {
      case 'weekly': return 'Esta Semana';
      case 'monthly': return 'Este Mes';
      case 'annual': return 'Este Año';
      default: return 'Todo el Tiempo';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="contabilidad-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contabilidad
          </h1>
          <p className="text-slate-500 mt-1">Control de ventas, pagos y ganancias • {getPeriodLabel()}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-40" data-testid="contabilidad-period-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo</SelectItem>
              <SelectItem value="daily">Diario</SelectItem>
              <SelectItem value="weekly">Semanal</SelectItem>
              <SelectItem value="monthly">Mensual</SelectItem>
              <SelectItem value="annual">Anual</SelectItem>
            </SelectContent>
          </Select>
          <ExportButton type="table" tableName="payments" className="hidden sm:flex" />
          <ExportButton type="table" tableName="profits" className="hidden sm:flex" />
          <Button 
            onClick={() => setIsPaymentDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="register-payment-btn"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Registrar Pago
          </Button>
          <Button 
            onClick={() => setIsProfitDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="register-profit-btn"
          >
            <PiggyBank className="w-4 h-4 mr-2" />
            Registrar Ganancia
          </Button>
        </div>
      </div>

      {/* Summary Cards (Iter 64 · Premium) */}
      {summary && (
        <div className={`grid grid-cols-1 sm:grid-cols-3 ${summary.total_commissions > 0 ? 'lg:grid-cols-4' : ''} gap-4`}>
          <PremiumStatCard
            label="Total Vendido (Gestores)"
            value={formatCurrency(summary.total_sales)}
            sublabel="Todos los gestores del período"
            icon={Users}
            tone="sky"
            testId="stat-total-sales"
          />
          <PremiumStatCard
            label="Pagos de Gestores"
            value={formatCurrency(summary.payments_from_gestores)}
            sublabel="Rendiciones cobradas"
            icon={Receipt}
            tone="teal"
            testId="stat-payments-received"
          />
          <PremiumStatCard
            label="Ganancias Registradas"
            value={formatCurrency(summary.manual_profits)}
            sublabel="Ingresos manuales del CEO"
            icon={TrendingUp}
            tone="emerald"
            testId="stat-manual-profits"
          />
          {summary.total_commissions > 0 && (
            <PremiumStatCard
              label="Comisiones Pagadas (CEO)"
              value={`-${formatCurrency(summary.total_commissions)}`}
              sublabel="Egresos por comisiones"
              icon={DollarSign}
              tone="rose"
              testId="stat-total-commissions"
            />
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="resumen">Saldo por Gestor</TabsTrigger>
          <TabsTrigger value="pagos">Pagos Recibidos</TabsTrigger>
          <TabsTrigger value="ganancias">Ganancias</TabsTrigger>
        </TabsList>

        {/* Saldo por Gestor */}
        <TabsContent value="resumen">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Saldo Vendido por Gestor
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Gestor</TableHead>
                    <TableHead className="text-right">Total Vendido</TableHead>
                    <TableHead className="text-right">Pagos Recibidos</TableHead>
                    <TableHead className="text-right">Saldo Pendiente</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedGestores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No hay gestores registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedGestores.map((gestor, index) => (
                      <TableRow key={gestor.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${index === 0 ? 'bg-yellow-100 text-yellow-700' : index === 1 ? 'bg-slate-200 text-slate-600' : index === 2 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-600'}`}>
                              {index < 3 ? `#${index + 1}` : gestor.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{gestor.name}</p>
                              <p className="text-xs text-slate-500">{gestor.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium text-slate-900">
                          {formatCurrency(gestor.total_sales)}
                        </TableCell>
                        <TableCell className="text-right text-purple-600 font-medium">
                          {formatCurrency(gestor.total_paid)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${gestor.pending_payment > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatCurrency(gestor.pending_payment)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/wallet/${gestor.id}`)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`wallet-btn-${gestor.id}`}
                          >
                            <Wallet className="w-4 h-4 mr-1" />
                            Wallet
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pagos Recibidos */}
        <TabsContent value="pagos">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Historial de Pagos Recibidos
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => setIsPaymentDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuevo Pago
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {payments.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No hay pagos registrados
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Gestor</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} className="hover:bg-slate-50">
                        <TableCell className="text-slate-600">{payment.payment_date}</TableCell>
                        <TableCell className="font-medium">{payment.gestor_name}</TableCell>
                        <TableCell className="text-slate-600">{payment.description || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-purple-600">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditPayment(payment)}
                              title="Editar pago"
                            >
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDeleteTarget({ type: 'payment', id: payment.id, name: payment.gestor_name });
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ganancias */}
        <TabsContent value="ganancias">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Registro de Ganancias
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => setIsProfitDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva Ganancia
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {profits.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No hay ganancias registradas
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profits.map((profit) => (
                      <TableRow key={profit.id} className="hover:bg-slate-50">
                        <TableCell className="text-slate-600">{profit.entry_date}</TableCell>
                        <TableCell className="font-medium">{profit.description || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatCurrency(profit.amount)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditProfit(profit)}
                              title="Editar ganancia"
                            >
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDeleteTarget({ type: 'profit', id: profit.id, name: 'esta ganancia' });
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <PaymentDialog
        open={isPaymentDialogOpen}
        onClose={handleClosePaymentDialog}
        onSubmit={handlePaymentSubmit}
        editingPayment={editingPayment}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        sortedGestores={sortedGestores}
        pricingGuides={pricingGuides}
        submitting={submitting}
      />

      {/* Profit Dialog */}
      <ProfitDialog
        open={isProfitDialogOpen}
        onClose={handleCloseProfitDialog}
        onSubmit={handleProfitSubmit}
        editingProfit={editingProfit}
        profitForm={profitForm}
        setProfitForm={setProfitForm}
        submitting={submitting}
      />

      {/* Delete Confirmation Dialog */}
      <ContabilidadDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        targetName={deleteTarget?.name}
      />
    </div>
  );
};

export default ContabilidadPage;
